/**
 * Reviewing imported eval cases.
 *
 * Every case arrives from the workbook inactive and asserting nothing, because
 * the workbook cannot say what SHOULD have happened. This screen is where a
 * person supplies that, and its whole design goal is throughput: there are
 * hundreds of these, and the difference between a good screen and a bad one is
 * days of somebody's life.
 *
 * So: the queue is sorted with the nearly-finished first, the transcript sits
 * beside the form rather than behind a click, and saving moves to the next case
 * automatically. Activation is refused — with the reason — while the case still
 * asserts nothing or contradicts itself.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, DownloadCloud, Inbox } from 'lucide-react';
import { ApiError, apiDelete, apiGet, apiPatch, apiPost } from '../api/client';
import { Badge, Button, Card, CardHeader } from '../components/ui/primitives';
import {
  EMPTY_FORM,
  FORBIDDABLE_ACTIONS,
  REVIEWABLE_TOOLS,
  describeExpectation,
  fromExpectation,
  toExpectation,
  validateExpectation,
  type ExpectationForm,
} from '../lib/eval-review/expectation';
import { BRAND_OPTIONS, INTENT_OPTIONS } from '../lib/eval-review/filing';

interface CaseRow {
  id: string;
  slug: string;
  title: string;
  intent: string;
  brandSlug: string;
  source: string;
  heldOut: boolean;
  active: boolean;
  notes: string | null;
  expected: Record<string, unknown> | null;
  updatedAt: string;
  /** False for a row the workbook recorded a verdict on but no conversation. */
  hasTranscript: boolean;
}

interface CasesResponse {
  cases: CaseRow[];
  counts: { total: number; active: number; heldOut: number; runnable: number };
}

interface CaseInput {
  channelEmail: string | null;
  messages: Array<{ role: string; body: string }>;
}

/** How much of the expectation is filled in — drives the queue order. */
function progressOf(row: CaseRow): number {
  return Object.keys(row.expected ?? {}).length;
}

export function EvalReviewPage() {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [form, setForm] = useState<ExpectationForm>(EMPTY_FORM);
  const [saved, setSaved] = useState<string | null>(null);
  const [showThin, setShowThin] = useState(false);
  // Drafts are the review queue; Active is maintenance. The second mode exists
  // because run #3's failure map sent a reviewer to EDIT live cases — outdated
  // caps, unreachable offer assertions — and there was no way to open one.
  const [mode, setMode] = useState<'drafts' | 'active'>('drafts');
  const [needle, setNeedle] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['eval-cases', mode],
    queryFn: () => apiGet<CasesResponse>(`/api/evals/cases?active=${mode === 'active'}`),
  });

  const queue = useMemo(() => {
    const query = needle.trim().toLowerCase();
    const rows = (data?.cases ?? []).filter((row) => {
      if (
        query !== '' &&
        !row.slug.toLowerCase().includes(query) &&
        !row.title.toLowerCase().includes(query) &&
        !row.intent.toLowerCase().includes(query)
      ) {
        return false;
      }
      // Runnable by default, drafts only. The workbook import produced 214
      // drafts and only 86 carry a conversation; the rest can never be
      // replayed, so putting them in the same queue spends a reviewer's
      // attention at the same rate on rows that cannot pay it back. They stay
      // reachable, deliberately — a thin case is sometimes still worth an
      // assertion. Active cases all ran, so the split means nothing there.
      if (mode === 'active') return true;
      return showThin ? !row.hasTranscript : row.hasTranscript;
    });
    // Drafts: nearly-done first — finishing something started beats starting
    // something new. Active: slug order, because the reviewer arrives here
    // FROM a failure map that names slugs.
    if (mode === 'active') return [...rows].sort((a, b) => a.slug.localeCompare(b.slug));
    return [...rows].sort((a, b) => progressOf(b) - progressOf(a) || a.slug.localeCompare(b.slug));
  }, [data, showThin, mode, needle]);

  const selected = queue.find((row) => row.id === selectedId) ?? queue[0] ?? null;

  useEffect(() => {
    if (selected) setForm(fromExpectation(selected.expected));
  }, [selected?.id]);

  const problems = validateExpectation(form);
  const canActivate = problems.length === 0;

  const discard = useMutation({
    mutationFn: () => apiDelete<{ deleted: number }>('/api/evals/cases/drafts'),
    onSuccess: async () => {
      setSelectedId(null);
      await queryClient.invalidateQueries({ queryKey: ['eval-cases'] });
    },
  });

  const save = useMutation({
    mutationFn: async ({ id, activate }: { id: string; activate: boolean }) =>
      apiPatch(`/api/evals/cases/${id}`, {
        expected: toExpectation(form),
        ...(activate ? { active: true } : {}),
      }),
    onSuccess: async (_result, variables) => {
      setSaved(variables.activate ? 'Activated' : 'Saved');
      await queryClient.invalidateQueries({ queryKey: ['eval-cases'] });
      if (variables.activate) {
        // Move on: the reviewer's next action is always the next case.
        const index = queue.findIndex((row) => row.id === variables.id);
        setSelectedId(queue[index + 1]?.id ?? null);
      }
    },
  });

  const deactivate = useMutation({
    mutationFn: async (id: string) => apiPatch(`/api/evals/cases/${id}`, { active: false }),
    onSuccess: async () => {
      setSaved('Deactivated — it leaves the suite and lands back in drafts');
      setSelectedId(null);
      await queryClient.invalidateQueries({ queryKey: ['eval-cases'] });
    },
  });

  if (isLoading) {
    return <p className="px-5 py-12 text-center text-sm text-ink-400">Loading…</p>;
  }

  return (
    <div className="mx-auto max-w-[1400px] px-5 py-5">
      {mode === 'drafts' ? (
        <>
          <EnrichBar />
          <DraftAssertionsBar />
        </>
      ) : null}
      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
      <Card className="h-fit">
        <CardHeader
          title={
            mode === 'active'
              ? 'Active cases'
              : showThin
                ? 'Drafts with no transcript'
                : 'Drafts you can run'
          }
          meta={
            mode === 'active'
              ? `${queue.length} in the suite — the yardstick the suite runs against`
              : `${queue.length} awaiting review · ${data?.counts.active ?? 0} active`
          }
          action={
            mode === 'active' ? undefined : (
            <button
              type="button"
              className="text-xs text-ink-400 underline hover:text-stop-700"
              onClick={() => {
                // Drafts only — an activated case is not reachable from here,
                // and the server scopes the delete the same way.
                if (
                  window.confirm(
                    `Discard all ${queue.length} drafts? Activated cases are not affected. This cannot be undone, but re-importing the workbook recreates them.`,
                  )
                ) {
                  discard.mutate();
                }
              }}
              disabled={discard.isPending}
            >
              {discard.isPending ? 'discarding…' : 'discard all'}
            </button>
            )
          }
        />
        <div className="flex gap-1 border-t border-line px-3 py-2">
          {(
            [
              ['Drafts', 'drafts'],
              ['Active', 'active'],
            ] as const
          ).map(([label, value]) => (
            <button
              key={value}
              type="button"
              onClick={() => {
                setMode(value);
                setSelectedId(null);
                setSaved(null);
              }}
              className={`rounded px-2 py-1 text-xs transition-colors duration-fast ${
                mode === value
                  ? 'bg-surface-sunken font-medium text-ink-900'
                  : 'text-ink-400 hover:text-ink-700'
              }`}
            >
              {label}
              {value === 'active' ? ` · ${data?.counts.active ?? 0}` : ''}
            </button>
          ))}
        </div>
        {mode === 'drafts' ? (
          <div className="flex gap-1 border-t border-line px-3 py-2">
            {(
              [
                ['Can run', false, data?.counts.runnable ?? 0],
                ['No transcript', true, (data?.counts.total ?? 0) - (data?.counts.runnable ?? 0)],
              ] as const
            ).map(([label, thin, count]) => (
              <button
                key={label}
                type="button"
                onClick={() => {
                  setShowThin(thin);
                  setSelectedId(null);
                }}
                className={`rounded px-2 py-1 text-xs transition-colors duration-fast ${
                  showThin === thin
                    ? 'bg-surface-sunken font-medium text-ink-900'
                    : 'text-ink-400 hover:text-ink-700'
                }`}
              >
                {label} · {count}
              </button>
            ))}
          </div>
        ) : null}
        <div className="border-t border-line px-3 py-2">
          <input
            value={needle}
            onChange={(e) => setNeedle(e.target.value)}
            placeholder="filter by slug, title or intent"
            className="w-full rounded border border-line bg-surface px-2 py-1.5 text-xs"
          />
        </div>

        {queue.length === 0 ? (
          <div className="border-t border-line px-4 py-8 text-center">
            <Inbox size={22} strokeWidth={1.5} className="mx-auto mb-2 text-ink-300" aria-hidden />
            <p className="text-xs text-ink-400">
              {needle.trim() !== ''
                ? 'Nothing matches that filter.'
                : mode === 'active'
                  ? 'No active cases yet — activate drafts to build the suite.'
                  : 'No drafts waiting. Import a workbook on the Evals page to create some.'}
            </p>
          </div>
        ) : null}
        <ul className="max-h-[70vh] overflow-y-auto">
          {queue.map((row) => (
            <li key={row.id}>
              <button
                type="button"
                onClick={() => setSelectedId(row.id)}
                className={`flex w-full items-start gap-2 border-t border-line px-3 py-2 text-left transition-colors duration-fast hover:bg-surface-sunken ${
                  row.id === selected?.id ? 'bg-surface-sunken' : ''
                }`}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-ink-700">{row.title}</p>
                  <p className="mt-0.5 truncate text-xs text-ink-400">
                    {row.intent} · {describeExpectation(row.expected)}
                  </p>
                </div>
                {row.heldOut ? <Badge tone="info">held out</Badge> : null}
              </button>
            </li>
          ))}
        </ul>
      </Card>

      {selected ? (
        <CaseEditor
          key={selected.id}
          row={selected}
          mode={mode}
          form={form}
          onChange={setForm}
          problems={problems}
          canActivate={canActivate}
          saving={save.isPending || deactivate.isPending}
          saved={saved}
          onSave={(activate) => {
            setSaved(null);
            save.mutate({ id: selected.id, activate });
          }}
          onDeactivate={() => {
            setSaved(null);
            deactivate.mutate(selected.id);
          }}
        />
      ) : null}
      </div>
    </div>
  );
}

/**
 * The step between importing and reviewing: drafts arrive carrying the
 * sheet's paraphrase, and this pulls the real Zendesk transcript plus the
 * customer's UltraCart order state into the next batch of them. Re-running
 * it is safe — done drafts are stamped and skipped, so each click works the
 * remainder.
 */
function EnrichBar() {
  const queryClient = useQueryClient();
  const [summary, setSummary] = useState<string | null>(null);
  const [failures, setFailures] = useState<Array<{ slug: string; reason: string }>>([]);

  const enrich = useMutation({
    mutationFn: () =>
      apiPost<{
        enriched: number;
        failed: Array<{ slug: string; reason: string }>;
        remaining: number;
        noTicketRef: number;
      }>('/api/evals/cases/enrich/batch', { limit: 20 }),
    onSuccess: async (result) => {
      setSummary(
        `Enriched ${result.enriched}` +
          (result.failed.length > 0 ? `, ${result.failed.length} failed` : '') +
          (result.remaining > 0
            ? ` — ${result.remaining} still waiting, run it again.`
            : result.noTicketRef > 0
              ? ` — done. ${result.noTicketRef} draft(s) carry no readable ticket id and can never enrich.`
              : ' — all drafts enriched.'),
      );
      setFailures(result.failed);
      await queryClient.invalidateQueries({ queryKey: ['eval-cases'] });
    },
    onError: (error: unknown) =>
      setSummary(error instanceof ApiError ? error.message : 'The batch did not run.'),
  });

  return (
    <Card className="mb-4">
      <CardHeader
        title="Enrich drafts"
        meta="pull each draft's real Zendesk transcript and UltraCart order state — 20 per click"
        action={
          <Button size="sm" disabled={enrich.isPending} onClick={() => enrich.mutate()}>
            <DownloadCloud size={13} strokeWidth={1.75} aria-hidden />
            {enrich.isPending ? 'Enriching…' : 'Enrich next 20'}
          </Button>
        }
      />
      {summary ? <p className="px-4 py-2.5 text-xs text-ink-700">{summary}</p> : null}
      {failures.length > 0 ? (
        <ul className="border-t border-line px-4 py-2.5 text-xs text-stop-700">
          {failures.slice(0, 5).map((failure) => (
            <li key={failure.slug}>
              <span className="font-mono">{failure.slug}</span>: {failure.reason}
            </li>
          ))}
          {failures.length > 5 ? (
            <li className="text-ink-400">…and {failures.length - 5} more — the audit log has all of them.</li>
          ) : null}
        </ul>
      ) : null}
    </Card>
  );
}

/**
 * AI drafts, a person decides. This pre-fills the same form the reviewer
 * sees — criterion trimmed to reply-facing demands, the deterministic ticks
 * where the note clearly implies them, blank where unsure — and never
 * activates anything. It also never touches a case a human has started.
 */
function DraftAssertionsBar() {
  const queryClient = useQueryClient();
  const [summary, setSummary] = useState<string | null>(null);
  const [failures, setFailures] = useState<Array<{ slug: string; reason: string }>>([]);

  const draft = useMutation({
    mutationFn: () =>
      apiPost<{
        drafted: number;
        failed: Array<{ slug: string; reason: string }>;
        remaining: number;
      }>('/api/evals/cases/assert/batch', { limit: 20 }),
    onSuccess: async (result) => {
      setSummary(
        `Drafted ${result.drafted}` +
          (result.failed.length > 0 ? `, ${result.failed.length} failed` : '') +
          (result.remaining > 0
            ? ` — ${result.remaining} untouched draft(s) still waiting, run it again.`
            : ' — every untouched draft now has a pre-filled form. Review and activate each; nothing was activated for you.'),
      );
      setFailures(result.failed);
      await queryClient.invalidateQueries({ queryKey: ['eval-cases'] });
    },
    onError: (error: unknown) =>
      setSummary(
        error instanceof ApiError
          ? error.message
          : 'The batch is taking a while — it keeps running on the server and each case saves as it lands. Refresh in a couple of minutes.',
      ),
  });

  return (
    <Card className="mb-4">
      <CardHeader
        title="Draft assertions"
        meta="AI pre-fills each form from the auditor's note and transcript — you still review and activate, 20 per click"
        action={
          <Button size="sm" disabled={draft.isPending} onClick={() => draft.mutate()}>
            <Check size={13} strokeWidth={1.75} aria-hidden />
            {draft.isPending ? 'Drafting…' : 'Draft next 20'}
          </Button>
        }
      />
      {summary ? <p className="px-4 py-2.5 text-xs text-ink-700">{summary}</p> : null}
      {failures.length > 0 ? (
        <ul className="border-t border-line px-4 py-2.5 text-xs text-stop-700">
          {failures.slice(0, 5).map((failure) => (
            <li key={failure.slug}>
              <span className="font-mono">{failure.slug}</span>: {failure.reason}
            </li>
          ))}
          {failures.length > 5 ? (
            <li className="text-ink-400">…and {failures.length - 5} more.</li>
          ) : null}
        </ul>
      ) : null}
    </Card>
  );
}

function CaseEditor({
  row,
  mode,
  form,
  onChange,
  problems,
  canActivate,
  saving,
  saved,
  onSave,
  onDeactivate,
}: {
  row: CaseRow;
  mode: 'drafts' | 'active';
  form: ExpectationForm;
  onChange: (form: ExpectationForm) => void;
  problems: string[];
  canActivate: boolean;
  saving: boolean;
  saved: string | null;
  onSave: (activate: boolean) => void;
  onDeactivate: () => void;
}) {
  const { data: detail } = useQuery({
    queryKey: ['eval-case', row.id],
    queryFn: () => apiGet<{ input: CaseInput }>(`/api/evals/cases/${row.id}`),
  });

  const set = <K extends keyof ExpectationForm>(key: K, value: ExpectationForm[K]) =>
    onChange({ ...form, [key]: value });

  const toggle = (key: 'requiredTools' | 'forbiddenActions', value: string) =>
    set(
      key,
      form[key].includes(value) ? form[key].filter((v) => v !== value) : [...form[key], value],
    );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title={row.title}
          meta={`${row.slug} · ${row.brandSlug || 'no brand'} · ${row.intent}`}
        />
        <FilingRow row={row} mode={mode} />
        <div className="space-y-3 px-4 py-4">
          {row.notes ? <p className="text-sm text-ink-500">{row.notes}</p> : null}
          <div className="space-y-2">
            {(detail?.input.messages ?? []).map((message, index) => (
              <div
                key={index}
                className={`rounded border border-line px-3 py-2 text-sm ${
                  message.role === 'customer' ? 'bg-surface-sunken' : ''
                }`}
              >
                <p className="mb-1 text-xs uppercase tracking-wide text-ink-400">{message.role}</p>
                <p className="whitespace-pre-wrap text-ink-700">{message.body}</p>
              </div>
            ))}
            {(detail?.input.messages ?? []).length === 0 ? (
              <p className="text-sm text-ink-400">
                This row had no transcript. You can still assert an outcome, but the case will be
                thin — consider whether it is worth activating.
              </p>
            ) : null}
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader title="What should have happened?" meta="Leave anything blank to not assert it" />
        {typeof row.expected?.assertionsDraftedAt === 'string' ? (
          <p className="border-b border-line bg-hold-100 px-4 py-2 text-xs text-hold-700">
            These assertions were pre-filled by AI from the auditor&rsquo;s note — check each
            before activating. Anything you change is yours; nothing is activated for you.
          </p>
        ) : null}
        <div className="grid gap-4 px-4 py-4 sm:grid-cols-2">
          {/* The judged half of the case. On import this is the auditor's own
              sentence; the judge scores the reply against each line. Editable
              because some notes need trimming to be about the REPLY, and some
              rows arrived with none. */}
          <div className="sm:col-span-2">
            <Field label="What the reply should have done (graded by the judge)">
              <textarea
                value={form.criteria}
                onChange={(e) => set('criteria', e.target.value)}
                rows={2}
                placeholder="one demand per line — e.g. Must say the Breeze purchase enrolls the customer in the Vitality Club trial"
                className="w-full rounded border border-line bg-surface px-2 py-1.5 text-sm"
              />
              <p className="mt-1 text-xs text-ink-400">
                Write demands about the reply itself, one per line — not about dashboard tags,
                escalation state, or earlier messages, which the judge cannot see.
              </p>
            </Field>
          </div>

          <Field label="Should a human take it?">
            <Choice
              value={form.escalate}
              onChange={(v) => set('escalate', v as ExpectationForm['escalate'])}
              options={[
                ['', 'not asserted'],
                ['yes', 'escalate'],
                ['no', 'agent handles it'],
              ]}
            />
          </Field>

          <Field label="Refund offered">
            <input
              value={form.offerPercent}
              onChange={(e) => set('offerPercent', e.target.value)}
              placeholder='blank, "none", or a percentage'
              className="w-full rounded border border-line bg-surface px-2 py-1.5 text-sm"
            />
          </Field>

          <Field label="Tools that must be called">
            <Checks
              options={REVIEWABLE_TOOLS as unknown as string[]}
              selected={form.requiredTools}
              onToggle={(v) => toggle('requiredTools', v)}
            />
          </Field>

          <Field label="Effects that must NOT happen">
            <Checks
              options={FORBIDDABLE_ACTIONS as unknown as string[]}
              selected={form.forbiddenActions}
              onToggle={(v) => toggle('forbiddenActions', v)}
            />
          </Field>

          <Field label="Expected tags">
            <input
              value={form.expectedTags}
              onChange={(e) => set('expectedTags', e.target.value)}
              placeholder="comma separated"
              className="w-full rounded border border-line bg-surface px-2 py-1.5 text-sm"
            />
          </Field>

          <Field label="Phrases the reply must not contain">
            <input
              value={form.forbiddenPhrases}
              onChange={(e) => set('forbiddenPhrases', e.target.value)}
              placeholder="autoship, 365-day"
              className="w-full rounded border border-line bg-surface px-2 py-1.5 text-sm"
            />
          </Field>

          <Field label="No money may move">
            <label className="flex items-center gap-2 text-sm text-ink-600">
              <input
                type="checkbox"
                checked={form.mustNotMoveMoney}
                onChange={(e) => set('mustNotMoveMoney', e.target.checked)}
              />
              refuse any refund in this case
            </label>
          </Field>

          <Field label="Held for approval">
            <Choice
              value={form.expectPendingApproval}
              onChange={(v) =>
                set('expectPendingApproval', v as ExpectationForm['expectPendingApproval'])
              }
              options={[
                ['', 'not asserted'],
                ['yes', 'should be held'],
                ['no', 'should not be held'],
              ]}
            />
          </Field>

          {/* The assertion that matters most on a cancellation, and the one
              this form was missing. Testers' loudest complaint about the vendor
              was being asked EIGHT TIMES before a cancellation was honoured;
              the save ladder exists to bound that, and `retention_conduct`
              grades it. */}
          <Field label="Should it try to save the subscription?">
            <Choice
              value={form.expectSaveAttempt}
              onChange={(v) => set('expectSaveAttempt', v as ExpectationForm['expectSaveAttempt'])}
              options={[
                ['', 'not asserted'],
                ['yes', 'offer a save first'],
                ['no', 'just cancel'],
              ]}
            />
            <p className="mt-1 text-xs text-ink-400">
              “Offer a save first” means pause, change frequency or skip a shipment before
              cancelling. Choose “just cancel” when the customer has already refused one, or asked
              plainly to stop.
            </p>
          </Field>

          <Field label="How many times may it ask?">
            <input
              value={form.maxSaveAttempts}
              onChange={(e) => set('maxSaveAttempts', e.target.value)}
              placeholder="blank = once"
              className="w-full rounded border border-line bg-surface px-2 py-1.5 text-sm text-ink-900"
            />
            <p className="mt-1 text-xs text-ink-400">
              Blank is the default of one. 0 forbids asking at all.
            </p>
          </Field>
        </div>

        {problems.length > 0 ? (
          <div className="border-t border-line bg-hold-100 px-4 py-3">
            {problems.map((problem) => (
              <p key={problem} className="flex items-start gap-2 text-sm text-hold-700">
                <AlertTriangle size={14} strokeWidth={1.75} className="mt-0.5 shrink-0" aria-hidden />
                {problem}
              </p>
            ))}
          </div>
        ) : null}

        <div className="flex items-center gap-2 border-t border-line px-4 py-3">
          {mode === 'active' ? (
            <>
              {/* An active case stays active on save: the edit is the point.
                  Activation problems still gate it — an active case must never
                  be saved into asserting nothing. */}
              <Button onClick={() => onSave(false)} disabled={!canActivate || saving}>
                <Check size={14} strokeWidth={2} aria-hidden />
                Save changes
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  if (
                    window.confirm(
                      'Deactivate this case? It leaves the suite and the next run measures one case fewer. Re-activating it later from Drafts undoes this.',
                    )
                  ) {
                    onDeactivate();
                  }
                }}
                disabled={saving}
              >
                Deactivate
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => onSave(true)} disabled={!canActivate || saving}>
                <Check size={14} strokeWidth={2} aria-hidden />
                Activate and next
              </Button>
              <Button variant="secondary" onClick={() => onSave(false)} disabled={saving}>
                Save draft
              </Button>
            </>
          )}
          {saved ? <span className="text-sm text-ok-600">{saved}</span> : null}
          <span className="ml-auto text-xs text-ink-400">
            {row.heldOut ? 'held-out slice' : 'tuning slice'}
          </span>
        </div>
      </Card>
    </div>
  );
}

/**
 * How the case is FILED — its intent and brand. Editable because the sheet
 * misfiled some: run #3 had a cancellation filed as tracking escalate five
 * times with "no shipment on file", and one case carried the wrong brand and
 * so an empty fixture. Intent can change while active (it is the routing the
 * case measures); brand cannot — the fixture was built from the brand's
 * UltraCart account, so the server refuses, and the path is deactivate →
 * refile → re-enrich → re-activate. Re-enrich sits beside it for exactly that.
 */
function FilingRow({ row, mode }: { row: CaseRow; mode: 'drafts' | 'active' }) {
  const queryClient = useQueryClient();
  const [intent, setIntent] = useState(row.intent);
  const [brand, setBrand] = useState(row.brandSlug);
  const [note, setNote] = useState<string | null>(null);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['eval-cases'] });
    await queryClient.invalidateQueries({ queryKey: ['eval-case', row.id] });
  };

  const update = useMutation({
    mutationFn: () =>
      apiPatch(`/api/evals/cases/${row.id}`, {
        ...(intent !== row.intent ? { intent } : {}),
        ...(brand !== row.brandSlug ? { brandSlug: brand } : {}),
      }),
    onSuccess: async () => {
      setNote(
        brand !== row.brandSlug
          ? 'Filing updated — now Re-enrich, so the fixture is rebuilt from the right brand.'
          : 'Filing updated.',
      );
      await refresh();
    },
    onError: (error: unknown) =>
      setNote(error instanceof ApiError ? error.message : 'Could not update the filing.'),
  });

  const enrich = useMutation({
    mutationFn: () => apiPost(`/api/evals/cases/${row.id}/enrich`, {}),
    onSuccess: async () => {
      setNote('Re-enriched — transcript and order state rebuilt from Zendesk and UltraCart.');
      await refresh();
    },
    onError: (error: unknown) =>
      setNote(error instanceof ApiError ? error.message : 'Could not re-enrich.'),
  });

  const dirty = intent !== row.intent || brand !== row.brandSlug;
  const busy = update.isPending || enrich.isPending;

  return (
    <div className="border-t border-line px-4 py-3">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Filed as intent">
          <select
            value={intent}
            onChange={(e) => setIntent(e.target.value)}
            className="rounded border border-line bg-surface px-2 py-1.5 text-sm"
          >
            {INTENT_OPTIONS.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Brand">
          <select
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            disabled={mode === 'active'}
            className="rounded border border-line bg-surface px-2 py-1.5 text-sm disabled:opacity-50"
          >
            {/* The stored value may be a legacy sheet code the list lacks. */}
            {BRAND_OPTIONS.includes(row.brandSlug) ? null : (
              <option value={row.brandSlug}>{row.brandSlug || '(none)'}</option>
            )}
            {BRAND_OPTIONS.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </Field>
        <Button size="sm" variant="secondary" disabled={!dirty || busy} onClick={() => update.mutate()}>
          Update filing
        </Button>
        {mode === 'drafts' ? (
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => enrich.mutate()}>
            <DownloadCloud size={13} strokeWidth={1.75} aria-hidden />
            Re-enrich
          </Button>
        ) : null}
      </div>
      {mode === 'active' ? (
        <p className="mt-1.5 text-xs text-ink-400">
          Brand is locked while the case is active — the fixture was built from that brand&rsquo;s
          UltraCart account. To change it: Deactivate, update the brand, Re-enrich, re-activate.
        </p>
      ) : null}
      {note ? <p className="mt-1.5 text-xs text-ink-700">{note}</p> : null}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-ink-400">{label}</p>
      {children}
    </div>
  );
}

function Choice({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (value: string) => void;
  options: Array<[string, string]>;
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {options.map(([key, label]) => (
        <button
          key={key}
          type="button"
          onClick={() => onChange(key)}
          className={`rounded border px-2 py-1 text-xs transition-colors duration-fast ${
            value === key
              ? 'border-accent-600 bg-accent-50 text-accent-700'
              : 'border-line text-ink-500 hover:bg-surface-sunken'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function Checks({
  options,
  selected,
  onToggle,
}: {
  options: string[];
  selected: string[];
  onToggle: (value: string) => void;
}) {
  return (
    <div className="space-y-1">
      {options.map((option) => (
        <label key={option} className="flex items-center gap-2 text-sm text-ink-600">
          <input
            type="checkbox"
            checked={selected.includes(option)}
            onChange={() => onToggle(option)}
          />
          <span className="font-mono text-xs">{option}</span>
        </label>
      ))}
    </div>
  );
}
